import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService as Auth0Service } from '@auth0/auth0-angular';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../../../services/auth/auth.service';
import { FrontendLoggerService } from '../../../services/frontend-logger.service';
import { Language, LanguageService } from '../../../services/language/language.service';
import { ThemeService } from '../../../services/theme/theme.service';
import { UserService } from '../../../services/user.service';
import { LocalizedCountry, UtilityService } from '../../../services/utility.service';
import { AuthWizardTimezoneCatalog, WizardTimezone } from '../auth-wizard-timezone.catalog';
import { SupportReferenceClipboard } from './support-reference.clipboard';
import { WizardOnboardingSubmitter } from './wizard-onboarding.submitter';
import { WizardOnboardingTokenVerifier } from './wizard-onboarding-token.verifier';
import { WizardPreferenceCoordinator } from './wizard-preference.coordinator';

interface WizardStep {
  title: string;
  description: string;
}

@Component({
  selector: 'app-wizard',
  templateUrl: './wizard.component.html',
  styleUrls: [
    './wizard.component.layout-and-progress.scss',
    './wizard.component.preferences-and-responsive.scss',
  ],
  standalone: false,
})
export class WizardComponent implements OnInit {
  currentStep = 0;
  wizardForm!: FormGroup;
  isLoading = false;
  errorMessage = '';
  supportReference: string | null = null;
  supportReferenceCopied = false;
  currentLanguage: Language = 'es';
  timezones: WizardTimezone[] = [];
  countries: LocalizedCountry[] = [];
  timezoneQuery = '';

  readonly steps: WizardStep[] = [
    { title: 'wizard.welcome_title', description: 'wizard.welcome_description' },
    { title: 'wizard.preferences_title', description: 'wizard.preferences_description' },
    { title: 'wizard.profile_title', description: 'wizard.profile_description' },
    { title: 'wizard.complete_title', description: 'wizard.complete_description' },
  ];

  readonly organizationTypes = [
    { value: 'enterprise', label: 'wizard.org_enterprise' },
    { value: 'education', label: 'wizard.org_education' },
    { value: 'government', label: 'wizard.org_government' },
    { value: 'nonprofit', label: 'wizard.org_nonprofit' },
    { value: 'other', label: 'wizard.org_other' },
  ];

  readonly organizationSizes = [
    { value: 'small', label: 'wizard.size_small' },
    { value: 'medium', label: 'wizard.size_medium' },
    { value: 'large', label: 'wizard.size_large' },
    { value: 'enterprise', label: 'wizard.size_enterprise' },
  ];

  private allTimezones: WizardTimezone[] = [];
  private readonly timezoneCatalog = new AuthWizardTimezoneCatalog();
  private readonly preferenceCoordinator: WizardPreferenceCoordinator;
  private readonly tokenVerifier: WizardOnboardingTokenVerifier;
  private readonly onboardingSubmitter: WizardOnboardingSubmitter;
  private readonly supportClipboard = new SupportReferenceClipboard();

  constructor(
    protected fb: FormBuilder,
    protected authService: AuthService,
    protected auth0: Auth0Service,
    protected userService: UserService,
    protected router: Router,
    public themeService: ThemeService,
    public languageService: LanguageService,
    protected utilityService: UtilityService,
    protected frontendLogger: FrontendLoggerService
  ) {
    this.preferenceCoordinator = new WizardPreferenceCoordinator(
      themeService,
      languageService,
      utilityService
    );
    this.tokenVerifier = new WizardOnboardingTokenVerifier(auth0, frontendLogger);
    this.onboardingSubmitter = new WizardOnboardingSubmitter(
      authService,
      auth0,
      userService,
      router,
      themeService,
      languageService,
      frontendLogger,
      this.tokenVerifier
    );
  }

  async ngOnInit(): Promise<void> {
    const theme = await firstValueFrom(this.themeService.currentTheme$);
    const language = await firstValueFrom(this.languageService.currentLang$);
    this.currentLanguage = language;
    this.wizardForm = this.fb.group({
      firstName: ['', Validators.required],
      secondName: ['', Validators.required],
      country: ['', Validators.required],
      jobTitle: ['', Validators.required],
      company: ['', Validators.required],
      organizationType: ['enterprise', Validators.required],
      organizationSize: ['small', Validators.required],
      timezone: ['', Validators.required],
      theme: [theme],
      language: [language],
      notifications: [true],
      dataCollection: [true],
    });

    this.refreshCountries(language);
    this.timezones = this.timezoneCatalog.build();
    this.allTimezones = [...this.timezones];
    const guessedTimezone = this.timezoneCatalog.guess(this.timezones);
    if (guessedTimezone) this.wizardForm.patchValue({ timezone: guessedTimezone });

    this.authService.getUserProfile().subscribe({
      next: (profile) => {
        if (!profile) return;
        this.wizardForm.patchValue({
          firstName: profile['firstName'] || '',
          secondName: profile['secondName'] || '',
          country: this.getLocalizedCountryName(profile['country'], this.currentLanguage),
          company: profile['company'] || '',
        });
      },
      error: (error) => this.frontendLogger.error('Error al cargar perfil de usuario', error),
    });
  }

  nextStep(): void {
    this.currentStep = this.preferenceCoordinator.nextStep(
      this.currentStep,
      this.steps.length,
      this.wizardForm
    );
  }

  previousStep(): void {
    this.currentStep = this.preferenceCoordinator.previousStep(this.currentStep);
  }

  setTheme(theme: 'light' | 'dark'): void {
    this.preferenceCoordinator.setTheme(this.wizardForm, theme);
  }

  setLanguage(language: Language): void {
    const state = this.preferenceCoordinator.setLanguage(this.wizardForm, language);
    this.currentLanguage = state.language;
    this.countries = state.countries;
  }

  onCountryChange(value: string): void {
    this.preferenceCoordinator.localizeCountry(this.wizardForm, value, this.currentLanguage);
  }

  get organizationTypeLabel(): string {
    return this.preferenceCoordinator.organizationLabel(
      this.wizardForm,
      'organizationType',
      this.organizationTypes,
      'wizard.org_other'
    );
  }

  get organizationSizeLabel(): string {
    return this.preferenceCoordinator.organizationLabel(
      this.wizardForm,
      'organizationSize',
      this.organizationSizes,
      'wizard.size_small'
    );
  }

  markFormGroupTouched(form: FormGroup): void {
    this.preferenceCoordinator.markFormGroupTouched(form);
  }

  translate(key: string): string {
    return this.preferenceCoordinator.translate(key);
  }

  onTimezoneQueryChange(value: string): void {
    this.timezoneQuery = value;
    this.timezones = this.preferenceCoordinator.filterTimezones(
      this.wizardForm,
      this.allTimezones,
      value
    );
  }

  onTimezoneEnter(): void {
    this.preferenceCoordinator.selectOnlyTimezone(this.wizardForm, this.timezones);
  }

  refreshCountries(language: Language): void {
    this.countries = this.preferenceCoordinator.refreshCountries(this.wizardForm, language);
  }

  getLocalizedCountryName(country: unknown, language: Language): string {
    return this.preferenceCoordinator.getLocalizedCountryName(country, language);
  }

  hasCompletedOnboardingClaims(claims: any): boolean {
    return this.tokenVerifier.hasCompletedOnboardingClaims(claims);
  }

  hasRoleClaim(claims: any): boolean { return this.tokenVerifier.hasRoleClaim(claims); }
  isTruthyFlag(value: unknown): boolean { return this.tokenVerifier.isTruthyFlag(value); }
  hasClaimValue(value: unknown): boolean { return this.tokenVerifier.hasClaimValue(value); }

  completeWizard(): void {
    this.supportReference = null;
    this.supportReferenceCopied = false;
    this.onboardingSubmitter.submit(this.wizardForm, {
      setLoading: (value) => { this.isLoading = value; },
      setError: (message) => { this.errorMessage = message; },
      handleProvisioningFailure: (error) => this.handleSignupProvisioningFailure(error),
      translate: (key) => this.translate(key),
    });
  }

  loginWithRedirectToHome(): Promise<void> {
    return this.onboardingSubmitter.loginWithRedirectToHome();
  }

  handleSignupProvisioningFailure(error: any): boolean {
    const presentation = this.onboardingSubmitter.describeProvisioningFailure(
      error,
      (key) => this.translate(key)
    );
    if (!presentation.handled) return false;
    this.errorMessage = presentation.message;
    this.supportReference = presentation.supportReference;
    return true;
  }

  async copySupportReference(): Promise<void> {
    if (!this.supportReference) return;
    try {
      await this.supportClipboard.copy(this.supportReference);
      this.supportReferenceCopied = true;
      setTimeout(() => { this.supportReferenceCopied = false; }, 2000);
    } catch (error) {
      this.frontendLogger.error(
        '[Wizard] Error copying support reference: ' + String((error as any)?.message || error),
        'Wizard.copySupportReference'
      );
    }
  }

}
